import React, { useEffect, useMemo, useState } from "react";

import { instructorSchedulesService } from "api/services/instructor-schedules.service";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";

import type { InstructorScheduleDetail, ScheduleStatus } from "api/types/instructor-schedules.types";
import "./ScheduleDetailModal.scss";

interface ScheduleDetailModalProps {
  open: boolean;
  scheduleId: number | null;
  onClose: () => void;
  onSaved?: () => void;
}

const APPROVAL_BADGE: Record<string, "pending" | "active" | "rejected"> = {
  PENDING: "pending",
  APPROVED: "active",
  REJECTED: "rejected",
};

const STATUS_BADGE: Record<string, "active" | "rejected" | "draft"> = {
  OPEN: "active",
  CLOSED: "draft",
  CANCELLED: "rejected",
};

const STATUS_OPTIONS: { value: ScheduleStatus; label: string }[] = [
  { value: "OPEN", label: "OPEN" },
  { value: "CLOSED", label: "CLOSED" },
  { value: "CANCELLED", label: "CANCELLED" },
];

const TICKET_TYPE_OPTIONS = [
  { value: "NONE", label: "NONE (일반 강습)" },
  { value: "SPECIAL_1", label: "SPECIAL_1" },
  { value: "SPECIAL_11", label: "SPECIAL_11" },
  { value: "SPECIAL_111", label: "SPECIAL_111" },
  { value: "ONE_DAY", label: "ONE_DAY" },
  { value: "GENERAL_1T10", label: "GENERAL_1T10" },
];

type EditableKey = "title" | "date" | "start_time" | "end_time" | "capacity" | "day_order" | "is_theory" | "instructor_comment" | "allowed_ticket_types" | "general_ticket_usage_count" | "status";

interface FormState {
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  day_order: number | null;
  is_theory: boolean;
  instructor_comment: string;
  allowed_ticket_types: string;
  general_ticket_usage_count: number;
  status: ScheduleStatus;
}

const formatDateTime = (value: string | null) => {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 16);
};

const toFormState = (d: InstructorScheduleDetail): FormState => ({
  title: d.title ?? "",
  date: d.date,
  start_time: d.start_time.slice(0, 5),
  end_time: d.end_time.slice(0, 5),
  capacity: d.capacity,
  day_order: d.day_order,
  is_theory: d.is_theory,
  instructor_comment: d.instructor_comment ?? "",
  allowed_ticket_types: d.allowed_ticket_types,
  general_ticket_usage_count: d.general_ticket_usage_count,
  status: d.status,
});

const getEditableKeys = (detail: InstructorScheduleDetail): Set<EditableKey> => {
  if (detail.approval_status !== "APPROVED") {
    return new Set<EditableKey>(["title", "date", "start_time", "end_time", "capacity", "day_order", "is_theory", "instructor_comment", "allowed_ticket_types", "general_ticket_usage_count", "status"]);
  }
  if (detail.current_bookings > 0) {
    return new Set<EditableKey>(["capacity", "status"]);
  }
  return new Set<EditableKey>(["capacity", "day_order", "start_time", "end_time", "is_theory", "title", "status"]);
};

interface FieldProps {
  label: string;
  full?: boolean;
  multiline?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, full, multiline, children }) => (
  <div className={`detail-field${full ? " detail-field--full" : ""}`}>
    <label className="detail-field__label">{label}</label>
    <div className={`detail-field__value${multiline ? " detail-field__value--multiline" : ""}`}>{children}</div>
  </div>
);

const ScheduleDetailModal: React.FC<ScheduleDetailModalProps> = ({ open, scheduleId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<InstructorScheduleDetail | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !scheduleId) return;
    let cancelled = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError("");
      setEditMode(false);
      try {
        const res = await instructorSchedulesService.get(scheduleId);
        if (cancelled) return;
        setDetail(res.data);
        setForm(toFormState(res.data));
      } catch (err) {
        if (cancelled) return;
        setDetail(null);
        setForm(null);
        setError(getApiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  const editableKeys = useMemo(() => (detail ? getEditableKeys(detail) : new Set<EditableKey>()), [detail]);
  const canEdit = (key: EditableKey) => editMode && editableKeys.has(key);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const buildPayload = (): Record<string, unknown> => {
    if (!detail || !form) return {};
    const payload: Record<string, unknown> = {};
    const original = toFormState(detail);

    editableKeys.forEach((key) => {
      const next = form[key];
      const prev = original[key];
      if (next !== prev) {
        if (key === "start_time" || key === "end_time") {
          payload[key] = `${form[key]}:00`;
        } else {
          payload[key] = next;
        }
      }
    });
    return payload;
  };

  const handleSave = async () => {
    if (!scheduleId || !form || !detail) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setEditMode(false);
      return;
    }
    setSaving(true);
    try {
      const res = await instructorSchedulesService.update(scheduleId, payload);
      setDetail(res.data);
      setForm(toFormState(res.data));
      setEditMode(false);
      onSaved?.();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (detail) setForm(toFormState(detail));
    setEditMode(false);
  };

  const renderEditNotice = () => {
    if (!editMode || !detail) return null;
    if (detail.approval_status !== "APPROVED") {
      return <div className="detail-notice detail-notice--info">승인 전 스케줄은 모든 항목을 수정할 수 있습니다.</div>;
    }
    if (detail.current_bookings > 0) {
      return <div className="detail-notice detail-notice--warn">예약자가 있는 승인 스케줄은 정원(capacity)과 스케줄 상태(status)만 수정 가능합니다.</div>;
    }
    return <div className="detail-notice detail-notice--info">승인된 스케줄(예약자 없음)은 정원, Day, 시간, 이론 여부, 특강명, 상태만 수정 가능합니다.</div>;
  };

  const renderContent = () => {
    if (loading) return <div className="admin-page__loading">불러오는 중...</div>;
    if (error) return <div className="admin-page__error">{error}</div>;
    if (!detail || !form) return null;

    const kind = detail.is_theory ? "이론" : detail.day_order !== null ? `실습 · Day ${detail.day_order}` : detail.title ? `특강 · ${detail.title}` : "실습";
    const associations = detail.associations_detail.length > 0 ? detail.associations_detail.map((a) => a.full_name || a.name).join(", ") : "-";
    const ticketInfoView = detail.allowed_ticket_types === "GENERAL_1T10" ? `${detail.allowed_ticket_types} (1회당 ${detail.general_ticket_usage_count}회 차감)` : detail.allowed_ticket_types;

    return (
      <form className="schedule-detail" onSubmit={(e) => e.preventDefault()}>
        {renderEditNotice()}

        <section className="detail-section">
          <h4 className="detail-section__title">기본 정보</h4>
          <div className="detail-section__grid">
            <Field label="강습명" full>
              {detail.lesson_product_display_name}
            </Field>
            <Field label="스포츠">{detail.sport_name || "-"}</Field>
            <Field label="강사">{detail.instructor_name}</Field>

            {canEdit("title") ? (
              <Field label="특강명" full>
                <input className="detail-input" type="text" value={form.title} onChange={(e) => updateField("title", e.target.value)} placeholder="특강명 (특강일 경우)" />
              </Field>
            ) : (
              <Field label="구분">{kind}</Field>
            )}

            {canEdit("day_order") ? (
              <Field label="Day">
                <input className="detail-input" type="number" min={1} value={form.day_order ?? ""} onChange={(e) => updateField("day_order", e.target.value === "" ? null : Number(e.target.value))} placeholder="Day (실습만)" />
              </Field>
            ) : null}

            {canEdit("is_theory") ? (
              <Field label="이론 여부">
                <label className="detail-check">
                  <input type="checkbox" checked={form.is_theory} onChange={(e) => updateField("is_theory", e.target.checked)} />
                  이론 수업
                </label>
              </Field>
            ) : null}

            {canEdit("allowed_ticket_types") ? (
              <Field label="허용 티켓">
                <select className="detail-input" value={form.allowed_ticket_types} onChange={(e) => updateField("allowed_ticket_types", e.target.value)}>
                  {TICKET_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="허용 티켓">{ticketInfoView}</Field>
            )}

            {canEdit("general_ticket_usage_count") && form.allowed_ticket_types === "GENERAL_1T10" ? (
              <Field label="차감 횟수">
                <input className="detail-input" type="number" min={1} value={form.general_ticket_usage_count} onChange={(e) => updateField("general_ticket_usage_count", Number(e.target.value))} />
              </Field>
            ) : null}
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">일정 · 장소</h4>
          <div className="detail-section__grid">
            <Field label="날짜">{canEdit("date") ? <input className="detail-input" type="date" value={form.date} onChange={(e) => updateField("date", e.target.value)} /> : detail.date}</Field>
            <Field label="시간">
              {canEdit("start_time") || canEdit("end_time") ? (
                <div className="detail-time-range">
                  <input className="detail-input" type="time" value={form.start_time} onChange={(e) => updateField("start_time", e.target.value)} disabled={!canEdit("start_time")} />
                  <span>~</span>
                  <input className="detail-input" type="time" value={form.end_time} onChange={(e) => updateField("end_time", e.target.value)} disabled={!canEdit("end_time")} />
                </div>
              ) : (
                `${detail.start_time.slice(0, 5)} ~ ${detail.end_time.slice(0, 5)}`
              )}
            </Field>
            <Field label="장소" full>
              {detail.location_name}
            </Field>
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">예약 현황</h4>
          <div className="detail-section__grid">
            <Field label="정원">{canEdit("capacity") ? <input className="detail-input" type="number" min={detail.current_bookings} value={form.capacity} onChange={(e) => updateField("capacity", Number(e.target.value))} /> : `${detail.capacity}명`}</Field>
            <Field label="예약 인원">
              {detail.current_bookings}명 (잔여 {detail.available_spots}명)
            </Field>
            <Field label="대기자">{detail.waiting_count}명</Field>
            <Field label="스케줄 상태">
              {canEdit("status") ? (
                <select className="detail-input" value={form.status} onChange={(e) => updateField("status", e.target.value as ScheduleStatus)}>
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <Badge variant={STATUS_BADGE[detail.status] ?? "draft"}>{detail.status}</Badge>
              )}
            </Field>
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">승인 정보</h4>
          <div className="detail-section__grid">
            <Field label="승인 상태">
              <Badge variant={APPROVAL_BADGE[detail.approval_status] ?? "draft"}>{detail.approval_status_display}</Badge>
            </Field>
            <Field label="승인자">{detail.approved_by_name ? `${detail.approved_by_name} (${formatDateTime(detail.approved_at)})` : "-"}</Field>
            {detail.rejection_reason && (
              <Field label="반려 사유" full multiline>
                {detail.rejection_reason}
              </Field>
            )}
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">부가 정보</h4>
          <div className="detail-section__grid">
            <Field label="협회" full>
              {associations}
            </Field>
            <Field label="강사 코멘트" full multiline>
              {canEdit("instructor_comment") ? <textarea className="detail-textarea" rows={4} value={form.instructor_comment} onChange={(e) => updateField("instructor_comment", e.target.value)} maxLength={2000} placeholder="강사 코멘트 (최대 2000자)" /> : detail.instructor_comment || "-"}
            </Field>
            {detail.practice_session_info?.has_practice_session && (
              <Field label="연결 연습반" full>
                {detail.practice_session_info.title} · 정원 {detail.practice_session_info.capacity}명 · {detail.practice_session_info.status}
              </Field>
            )}
          </div>
        </section>

        <section className="detail-section">
          <h4 className="detail-section__title">기록</h4>
          <div className="detail-section__grid">
            <Field label="생성일">{formatDateTime(detail.created_at)}</Field>
            <Field label="수정일">{formatDateTime(detail.updated_at)}</Field>
          </div>
        </section>
      </form>
    );
  };

  const renderFooter = () => {
    if (!detail) return null;
    if (editMode) {
      return (
        <div className="modal-footer-actions">
          <Button variant="secondary" onClick={handleCancelEdit} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || editableKeys.size === 0}>
            {saving ? "저장 중..." : "저장"}
          </Button>
        </div>
      );
    }
    return (
      <div className="modal-footer-actions">
        <Button variant="primary" onClick={() => setEditMode(true)}>
          수정하기
        </Button>
      </div>
    );
  };

  return (
    <Modal open={open} title="강습 상세" width="760px" onClose={onClose} footer={renderFooter()} closeOnOverlayClick={false}>
      {renderContent()}
    </Modal>
  );
};

export default ScheduleDetailModal;
